import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { normalizeMarkdownInput } from '../../utils/markdownNormalize';

function cn(...parts) {
  return parts.filter(Boolean).join(' ');
}

/** Remove common inline markdown markers for plain-text contexts (alt text, confirm dialogs). */
export function stripInlineMarkdown(s) {
  if (s == null || typeof s !== 'string') return '';
  return s
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/`(.+?)`/g, '$1');
}

const listBlockClasses = {
  ul: ({ node, children, className, ...props }) => (
    <ul
      {...props}
      className={cn(
        'my-2 list-disc list-outside pl-5 text-ink marker:text-ink-subtle',
        'space-y-1 [&_ul]:my-1.5 [&_ol]:my-1.5 [&_ul]:list-[circle] [&_ul_ul]:list-[square]',
        '[&_li>p]:mb-1 [&_li>p:last-child]:mb-0',
        className
      )}
    >
      {children}
    </ul>
  ),
  ol: ({ node, children, className, ...props }) => (
    <ol
      {...props}
      className={cn(
        'my-2 list-decimal list-outside pl-5 text-ink marker:text-ink-subtle',
        'space-y-1 [&_ul]:my-1.5 [&_ol]:my-1.5',
        '[&_li>p]:mb-1 [&_li>p:last-child]:mb-0',
        className
      )}
    >
      {children}
    </ol>
  ),
  li: ({ node, children, className, ...props }) => (
    <li {...props} className={cn('leading-relaxed pl-0.5', className)}>
      {children}
    </li>
  ),
  input: ({ node, className, ...props }) => (
    <input
      {...props}
      className={cn(
        'mr-2 mt-0.5 inline-block h-4 w-4 shrink-0 cursor-default rounded border-edge-strong align-middle text-link',
        className
      )}
    />
  )
};

function inlineComponents(variant) {
  // Headings use font-bold (700); strong must be heavier than parent, not lighter (semibold 600).
  const strongClass =
    variant === 'heading' ? '!font-black !text-ink' : '!font-semibold !text-ink';

  const emphasis = {
    strong: ({ node, children, className, ...props }) => (
      <strong {...props} className={cn(strongClass, className)}>
        {children}
      </strong>
    ),
    em: ({ node, children, className, ...props }) => (
      <em {...props} className={cn('!italic !text-ink', className)}>
        {children}
      </em>
    ),
    del: ({ node, children, className, ...props }) => (
      <del {...props} className={cn('!line-through !text-ink-muted', className)}>
        {children}
      </del>
    ),
    s: ({ node, children, className, ...props }) => (
      <s {...props} className={cn('!line-through !text-ink-muted', className)}>
        {children}
      </s>
    )
  };

  if (variant === 'heading') {
    // No ul/ol here — titles render inside <span>, block lists are invalid inside phrasing content.
    return {
      ...emphasis,
      p: ({ children }) => <>{children}</>
    };
  }

  return {
    ...emphasis,
    ...listBlockClasses,
    p: ({ node, children, className, ...props }) => (
      <p {...props} className={cn('mb-2 text-ink last:mb-0', className)}>
        {children}
      </p>
    )
  };
}

/**
 * Renders short inline markdown (bold / italic / strikethrough). Unwraps block `p` so this can sit inside headings.
 * @param {'heading' | 'body'} [variant='body'] — use `heading` inside bold titles so ** stands out from the parent weight.
 * @param {keyof JSX.IntrinsicElements} [as] — wrapper tag; default `span` for heading, `div` for body (lists need a block wrapper).
 */
export default function InlineMarkdown({ children, as, className, variant = 'body' }) {
  if (children == null || children === '') return null;
  const content = normalizeMarkdownInput(String(children));
  const Tag = as ?? (variant === 'heading' ? 'span' : 'div');
  return (
    <Tag className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={inlineComponents(variant)}>
        {content}
      </ReactMarkdown>
    </Tag>
  );
}
